import React, { useState, useEffect, useRef } from 'react';

const InfoDialog = ({ 
  isOpen, 
  title, 
  description, 
  actions, 
  customContent, 
  showActions = true,
  progressSteps = null
}) => {
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const timeoutRef = useRef(null);
  const hasCompletedRef = useRef(false);

  // Handle progress steps automatically
  useEffect(() => {
    if (!isOpen || !progressSteps) {
      setCurrentProgressStep(0);
      hasCompletedRef.current = false;
      return;
    }

    // Don't restart if already completed
    if (hasCompletedRef.current) {
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset to step 0
    setCurrentProgressStep(0);

    // Execute steps sequentially with fixed short delays
    const executeSteps = async () => {
      for (let i = 0; i < progressSteps.length; i++) {
        await new Promise(resolve => {
          timeoutRef.current = setTimeout(() => {
            setCurrentProgressStep(i + 1);
            resolve();
          }, progressSteps[i].time); // Fixed 300ms delay for visual effect
        });
      }
      
      // Mark as completed to prevent restart
      hasCompletedRef.current = true;
    };

    executeSteps();

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen, progressSteps]);

  // Reset completion state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      hasCompletedRef.current = false;
      setCurrentProgressStep(0);
      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [isOpen]);

  const renderProgressSteps = () => {
    if (!progressSteps) return null;

    return (
      <div className="space-y-4">
        {progressSteps.map((step, index) => {
          const isCompleted = index < currentProgressStep;
          const isCurrent = index === currentProgressStep;
          
          return (
            <div key={index} className="flex items-start space-x-3">
              {/* Step Icon */}
              <div className="flex-shrink-0 mt-1">
                {isCompleted ? (
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                ) : isCurrent ? (
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
                  </div>
                ) : (
                  <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                  </div>
                )}
              </div>
              
              {/* Step Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${
                  isCompleted ? 'text-green-600' : 
                  isCurrent ? 'text-blue-600' : 
                  'text-gray-500'
                }`}>
                  {step.title}
                </p>
                <p className={`text-xs ${
                  isCompleted ? 'text-green-500' : 
                  isCurrent ? 'text-blue-500' : 
                  'text-gray-400'
                }`}>
                  {step.description}
                </p>
                
                {/* Progress Bar for Current Step */}
                {isCurrent && (
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
                    <div 
                      className="bg-blue-500 h-1 rounded-full transition-all duration-1000 ease-out"
                      style={{ width: '60%' }}
                    ></div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-800 dark:bg-opacity-75 flex justify-center items-center z-50">
      <div className="bg-white dark:bg-gray-900 p-8 rounded-lg shadow-lg w-11/12 md:w-1/3">
        <h2 className="text-xl font-bold mb-4 dark:text-white">{title}</h2>
        
        {customContent ? (
          customContent
        ) : progressSteps ? (
          <div className="text-center py-6">
            <div className="mb-6">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-lg font-medium text-blue-600">
                Processing Your Results...
              </p>
            </div>
            <div className="text-left">
              {renderProgressSteps()}
            </div>
          </div>
        ) : (
          <div className="mb-4 dark:text-gray-200" dangerouslySetInnerHTML={{ __html: description }}></div>
        )}
        
        {showActions && actions && (
          <div className="flex justify-end space-x-4">
            {actions.map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                className={`px-4 py-2 rounded-lg transition ${action.className}`}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InfoDialog;